import { withStyles } from "@mui/styles";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEmployeeTimeOffRequestSnapshotsByStation,
  fetchTimeOffRequestSnapshotByReferenceNumber,
} from "../api/timeOffRequest";
import { isUserAuthorizedByServer } from "../api/userAccess";
import EmployeeTimeOffRequestTable from "../components/EmployeeTimeOffRequestTable";
import PageLoading from "../components/PageLoading";
import TimeOffFilterFormDesktop from "../components/TimeOffFilterFormDesktop";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import {
  selectAdminTimeOffRequestData,
  updateAdminTimeOffRequestFoundByReferenceNumberSearch,
  updateAdminTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen,
  updateAdminTimeOffRequestFoundByReferenceNumberSearchIsLoading,
  updateDateTypeFilter,
  updateEndDateFilter,
  updateEndDateFilterErrors,
  updateSearchString,
  updateSearchStringErrors,
  updateStartDateFilter,
  updateStartDateFilterErrors,
  updateStatusFilters,
  updateTimeOffTypeFilters,
} from "../slices/adminTimeOfRequest/adminTimeOfRequestSlice";
import { selectWorkgroupData } from "../slices/commonConfig/commonConfigSlice";
import { selectStationFilter, selectUserSession, selectWorkgroupFilters, updateStationFilter, updateWorkgroupFilters } from "../slices/userSession/userSessionSlice";
import { selectWebSocketEventData } from "../slices/webSocketEvent/webSocketEventSlice";
import { logout } from "../util/authentication";
import { getAdminStation, getMultiStationAdminStationList } from "../util/commonUtils";
import {
  ADMIN_ALL_TIME_OFF_TYPES,
  ALL_TIME_OFF_STATUSES,
  BREADCRUMBS_TITLE,
  ERROR_MAX_PAYLOAD_LIMIT_MSG,
  HOME_PATH,
} from "../util/constants";
import { deserializeDateFromRedux, serializeDateToRedux, set_time_to_end_of_day } from "../util/dateTimeFormatter";
import { timeOffRequestDataErrorCodeToErrorMessageMap } from "../util/errorMapper";
import {
  DEFAULT_ADMIN_TIME_OFF_END_DATE_FILTER,
  DEFAULT_EMPLOYEE_TIME_OFF_START_DATE_FILTER,
  filterTimeOffRequests,
} from "../util/filterTimeOffRequests";
import { validateEndDateFilter, validateStartDateFilter } from "../util/inputValidation";
import { ADMIN_MOBILE_WARNING_MESSAGE } from "../util/messageConstants";
import { initializeSearchIndex, searchTimeOffRequests } from "../util/searchTimeOffRequests";
import { getScrubbedTimeOffRequestSnapshot, getScrubbedTimeOffRequestSnapshots } from "../util/timeOffRequestScrubber";
import { mergeWebSocketAdminTORs } from "../util/webSocketUtils";

const styles = () => ({
  font: {
    fontSize: "32px",
  },
});
const TimeOffAdministrationPage = (props) => {
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const workgroupFilters = useSelector(selectWorkgroupFilters);
  const adminTimeOffRequestData = useSelector(selectAdminTimeOffRequestData);
  const webSocketEventData = useSelector(selectWebSocketEventData);
  const workgroupData = useSelector(selectWorkgroupData);
  const dispatch = useDispatch();
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorObject, setErrorObject] = useState({});
  const [stationTimeOffRequests, setStationTimeOffRequests] = useState([]);
  const [filteredStationTimeOffRequest, setFilteredStationTimeOffRequests] = useState([]);
  const [searchedStationTimeOffRequest, setSearchedStationTimeOffRequests] = useState([]);
  const [isSearch, setIsSearch] = useState(false);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  // We need these Refs because changes to them are synchronous (as opposed to the state variables being async) and
  // knowing that, we can check the value of them upon getting a response back to ensure queries from changing the
  // table filters are not overwritten in the event a filter is changed in the middle of waiting for a response
  // See doesResponseQueryParamsMatchCurrentFilters and how it is used for more details.
  const stationFilterRef = useRef("");
  const dateTypeFilterRef = useRef("");
  const startDateFilterRef = useRef(null);
  const endDateFilterRef = useRef(null);
  const tempWebSocketDataRef = useRef({});

  const torTableDesktopRef = useGridApiRef();
  const isAdminPageRef = useRef(true);
  const isApiSubscribedRef = useRef(true);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      if (adminTimeOffRequestData.startDateFilter === null) {
        initializeFilterState();
      }
      stationFilterRef.current = stationFilter;
      if (
        adminTimeOffRequestData?.startDateFilterErrors === "" &&
        adminTimeOffRequestData?.endDateFilterErrors === ""
      ) {
        updateStationTimeOffRequestList(
          userSession.eid,
          stationFilter,
          adminTimeOffRequestData.dateTypeFilter,
          getStartDateFilterSafely(),
          getEndDateFilterSafely()
        );
      } else {
        setIsLoading(false);
      }
    }
    return () => {
      // Cleaning up lingering Promise handling...
      isApiSubscribedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  useEffect(() => {
    tempWebSocketDataRef.current = webSocketEventData.torAdminEvents;
    if (Object.values(webSocketEventData.torAdminEvents).length > 0) {
      if (!isFirstLoad) {
        setStationTimeOffRequests((prevState) => {
          return mergeWebSocketAdminTORs(webSocketEventData.torAdminEvents, prevState);
        });
        tempWebSocketDataRef.current = {};
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketEventData.torAdminEvents]);

  useEffect(() => {
    let newFilteredTimeOffRequests = filterTimeOffRequests(
      stationTimeOffRequests,
      adminTimeOffRequestData.dateTypeFilter,
      getStartDateFilterSafely(),
      getEndDateFilterSafely(),
      adminTimeOffRequestData.timeOffTypeFilters,
      adminTimeOffRequestData.statusFilters,
      workgroupFilters,
      isAdminPageRef.current
    );
    applyFilters(newFilteredTimeOffRequests);
    if (adminTimeOffRequestData.searchString !== "") {
      handleSearchSubmit(adminTimeOffRequestData.searchString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationTimeOffRequests]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(userSession.isAdmin);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  // // We need this initialization since these fields are dependent on the stationFilter value
  const initializeFilterState = () => {
    dispatch(updateStartDateFilter(serializeDateToRedux(DEFAULT_EMPLOYEE_TIME_OFF_START_DATE_FILTER(stationFilter))));
    dispatch(updateEndDateFilter(serializeDateToRedux(DEFAULT_ADMIN_TIME_OFF_END_DATE_FILTER(stationFilter))));
  };

  // These safe functions are used to be sure the default value is provided in the event the redux state is not available yet
  const getStartDateFilterSafely = () => {
    return adminTimeOffRequestData.startDateFilter
      ? deserializeDateFromRedux(adminTimeOffRequestData.startDateFilter)
      : DEFAULT_EMPLOYEE_TIME_OFF_START_DATE_FILTER(stationFilter);
  };

  const getEndDateFilterSafely = () => {
    return adminTimeOffRequestData.endDateFilter
      ? deserializeDateFromRedux(adminTimeOffRequestData.endDateFilter)
      : DEFAULT_ADMIN_TIME_OFF_END_DATE_FILTER(stationFilter);
  };

  const updateStationTimeOffRequestList = (eid, station, dateType, startDate, endDate) => {
    isApiSubscribedRef.current = true;
    setLoading(true);
    setIsError(false);
    setErrorObject({});
    dateTypeFilterRef.current = dateType;
    startDateFilterRef.current = startDate;
    endDateFilterRef.current = endDate;
    fetchEmployeeTimeOffRequestSnapshotsByStation(eid, station, dateType, startDate, endDate)
      .then((res) => res.json())
      .then((result) => {
        if (isApiSubscribedRef.current && doesResponseQueryParamsMatchCurrentFilters(result.queryParams)) {
          if (result.message) {
            if (result.message === ERROR_MAX_PAYLOAD_LIMIT_MSG) {
              setIsError(true);
              setErrorObject(timeOffRequestDataErrorCodeToErrorMessageMap.timeOffRequestDataMaxPayloadError);
            } else {
              throw new Error("Unexpected error");
            }
          } else {
            let scrubbedTimeOffRequests = getScrubbedTimeOffRequestSnapshots(result.timeOffRequestSnapshots);
            setStationTimeOffRequests(mergeWebSocketAdminTORs(tempWebSocketDataRef.current, scrubbedTimeOffRequests));
            tempWebSocketDataRef.current = {};
            setIsLoading(false);
            setIsFirstLoad(false);
          }
        }
      })
      .catch((err) => {
        console.error("error happened!", err);
        setIsLoading(false);
        setIsError(true);
        setErrorObject(timeOffRequestDataErrorCodeToErrorMessageMap.unexpectedTimeOffRequestDataError);
      });
  };

  const doesResponseQueryParamsMatchCurrentFilters = (responseQueryParams) => {
    return (
      responseQueryParams.station === stationFilterRef.current &&
      decodeURI(responseQueryParams.dateType) === dateTypeFilterRef.current &&
      responseQueryParams.startDate === startDateFilterRef.current.toMillis().toString() &&
      responseQueryParams.endDate === endDateFilterRef.current.toMillis().toString()
    );
  };

  const applySearch = (searchString, searchedTimeOffRequests) => {
    if (searchString === "") {
      setIsSearch(false);
      setSearchedStationTimeOffRequests(searchedTimeOffRequests);
      applyAdminTimeOffTableScroll();
      setLoading(false);
    } else {
      setIsSearch(true);
      setSearchedStationTimeOffRequests(searchedTimeOffRequests);
      applyAdminTimeOffTableScroll();
      setLoading(false);
    }
  };

  const applyFilters = (filteredTrades) => {
    initializeSearchIndex(filteredTrades, isAdminPageRef.current);
    if (isSearch) {
      searchTimeOffRequests(adminTimeOffRequestData.searchString, applySearch, isAdminPageRef.current);
    }
    setFilteredStationTimeOffRequests(filteredTrades);
  };

  const applyAdminTimeOffTableScroll = () => {
    // This ref scrolls desktop trades to top
    const currentDesktop = torTableDesktopRef.current;
    if (currentDesktop !== null && currentDesktop.getRowModels && currentDesktop.getRowModels().size > 0) {
      currentDesktop.scrollToIndexes({ rowIndex: 0, colIndex: 0 });
    }
  };

  const setLoading = (isLoading) => {
    if (!isFirstLoad) {
      setIsLoading(isLoading);
    }
  };

  const handleDateTypeFilterChange = (event) => {
    updateStationTimeOffRequestList(
      userSession.eid,
      stationFilter,
      event.target.value,
      deserializeDateFromRedux(adminTimeOffRequestData.startDateFilter),
      deserializeDateFromRedux(adminTimeOffRequestData.endDateFilter)
    );
    dispatch(updateDateTypeFilter(event.target.value));
    applyAdminTimeOffTableScroll();
  };

  const handleTimeOffTypeFilterChange = (event) => {
    let newTimeOffTypeFilterSelectedList = event.target.value;
    if (newTimeOffTypeFilterSelectedList.includes("Select All")) {
      if (isTimeOffTypeSelectAllChecked()) {
        newTimeOffTypeFilterSelectedList = [];
      } else {
        newTimeOffTypeFilterSelectedList = ADMIN_ALL_TIME_OFF_TYPES;
      }
    }
    setLoading(true);
    let newFilteredTimeOffRequests = filterTimeOffRequests(
      stationTimeOffRequests,
      adminTimeOffRequestData.dateTypeFilter,
      getStartDateFilterSafely(),
      getEndDateFilterSafely(),
      newTimeOffTypeFilterSelectedList,
      adminTimeOffRequestData.statusFilters,
      workgroupFilters,
      isAdminPageRef.current
    );
    dispatch(updateTimeOffTypeFilters(newTimeOffTypeFilterSelectedList));
    applyFilters(newFilteredTimeOffRequests);
    setLoading(false);
    applyAdminTimeOffTableScroll();
  };

  const handleStartDateFilterChange = (date) => {
    let newStartDateFilterErrors = validateStartDateFilter(
      date,
      deserializeDateFromRedux(adminTimeOffRequestData.endDateFilter),
      stationFilter
    );
    dispatch(updateStartDateFilter(serializeDateToRedux(date)));
    dispatch(updateStartDateFilterErrors(newStartDateFilterErrors));
    if (newStartDateFilterErrors === "") {
      updateStationTimeOffRequestList(
        userSession.eid,
        stationFilter,
        adminTimeOffRequestData.dateTypeFilter,
        date,
        deserializeDateFromRedux(adminTimeOffRequestData.endDateFilter)
      );
      dispatch(updateEndDateFilterErrors(""));
      applyAdminTimeOffTableScroll();
    }
  };

  const handleEndDateFilterChange = (event) => {
    let date = set_time_to_end_of_day(event);
    let newEndDateFilterErrors = validateEndDateFilter(
      date,
      deserializeDateFromRedux(adminTimeOffRequestData.startDateFilter),
      stationFilter
    );
    dispatch(updateEndDateFilter(serializeDateToRedux(date)));
    dispatch(updateEndDateFilterErrors(newEndDateFilterErrors));
    if (newEndDateFilterErrors === "") {
      updateStationTimeOffRequestList(
        userSession.eid,
        stationFilter,
        adminTimeOffRequestData.dateTypeFilter,
        deserializeDateFromRedux(adminTimeOffRequestData.startDateFilter),
        date
      );
      dispatch(updateStartDateFilterErrors(""));
      applyAdminTimeOffTableScroll();
    }
  };

  const isTimeOffTypeSelectAllChecked = () => {
    return adminTimeOffRequestData.timeOffTypeFilters.length === ADMIN_ALL_TIME_OFF_TYPES.length;
  };

  const isTimeOffTypeSelectAllIndeterminate = () => {
    return (
      adminTimeOffRequestData.timeOffTypeFilters.length !== ADMIN_ALL_TIME_OFF_TYPES.length &&
      adminTimeOffRequestData.timeOffTypeFilters.length !== 0
    );
  };

  const handleStatusFilterChange = (event) => {
    let newStatusFilterSelectedList = event.target.value;
    if (newStatusFilterSelectedList.includes("Select All")) {
      if (isStatusSelectAllChecked()) {
        newStatusFilterSelectedList = [];
      } else {
        newStatusFilterSelectedList = ALL_TIME_OFF_STATUSES;
      }
    }
    setLoading(true);
    let newFilteredTimeOffRequests = filterTimeOffRequests(
      stationTimeOffRequests,
      adminTimeOffRequestData.dateTypeFilter,
      getStartDateFilterSafely(),
      getEndDateFilterSafely(),
      adminTimeOffRequestData.timeOffTypeFilters,
      newStatusFilterSelectedList,
      workgroupFilters,
      isAdminPageRef.current
    );
    dispatch(updateStatusFilters(newStatusFilterSelectedList));
    applyFilters(newFilteredTimeOffRequests);
    setLoading(false);
    applyAdminTimeOffTableScroll();
  };

  const isStatusSelectAllChecked = () => {
    return adminTimeOffRequestData.statusFilters.length === ALL_TIME_OFF_STATUSES.length;
  };

  const isStatusSelectAllIndeterminate = () => {
    return (
      adminTimeOffRequestData.statusFilters.length !== ALL_TIME_OFF_STATUSES.length &&
      adminTimeOffRequestData.statusFilters.length !== 0
    );
  };

  const handleWorkgroupFilterChange = (event) => {
    let newWorkgroupFilterSelectedList = event.target.value;
    if (newWorkgroupFilterSelectedList.includes("Select All")) {
      if (isWorkgroupSelectAllChecked()) {
        newWorkgroupFilterSelectedList = [];
      } else {
        newWorkgroupFilterSelectedList = Object.keys(workgroupData);
      }
    }
    setLoading(true);
    let newFilteredTimeOffRequests = filterTimeOffRequests(
      stationTimeOffRequests,
      adminTimeOffRequestData.dateTypeFilter,
      deserializeDateFromRedux(adminTimeOffRequestData.startDateFilter),
      deserializeDateFromRedux(adminTimeOffRequestData.endDateFilter),
      adminTimeOffRequestData.timeOffTypeFilters,
      adminTimeOffRequestData.statusFilters,
      newWorkgroupFilterSelectedList,
      workgroupFilters,
      isAdminPageRef.current
    );
    dispatch(updateWorkgroupFilters(newWorkgroupFilterSelectedList));
    applyFilters(newFilteredTimeOffRequests);
    setLoading(false);
    applyAdminTimeOffTableScroll();
  };

  const isWorkgroupSelectAllIndeterminate = () => {
    return (
      workgroupFilters.length !== Object.keys(workgroupData).length &&
      workgroupFilters.length !== 0
    );
  };

  const isWorkgroupSelectAllChecked = () => {
    return workgroupFilters.length === Object.keys(workgroupData).length;
  };

  const handleStationFilterChange = (event) => {
    dispatch(updateStationFilter(event.target.value));
    stationFilterRef.current = event.target.value;
    setIsLoading(true);
    updateStationTimeOffRequestList(
      userSession.eid,
      event.target.value,
      dateTypeFilterRef.current,
      startDateFilterRef.current,
      endDateFilterRef.current
    );
    applyAdminTimeOffTableScroll();
  };

  const handleSearchChange = (event) => {
    dispatch(updateSearchString(event.target.value));
  };

  const handleSearchBlur = (event) => {
    if (event.target.value.trim() === "") {
      handleSearchSubmit("");
    }
  };

  const handleSearchEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit(adminTimeOffRequestData.searchString);
    }
  };

  const handleSearchCancel = () => {
    dispatch(updateSearchString(""));
    handleSearchSubmit("");
  };

  const handleSearchSubmit = (newSearchString) => {
    dispatch(updateSearchStringErrors(""));
    const newSearchStringTrimmed = newSearchString.trim();
    if (
      !isNaN(parseInt(newSearchStringTrimmed)) &&
      parseInt(newSearchStringTrimmed) >= 10000000 &&
      parseInt(newSearchStringTrimmed) <= 99999999
    ) {
      dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsLoading(true));
      fetchTimeOffRequestSnapshotByReferenceNumber(
        newSearchStringTrimmed,
        userSession.eid,
        stationFilter,
        isAdminPageRef.current
      )
        .then((result) => {
          if (result.message) {
            dispatch(updateSearchStringErrors(result.message));
            dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
          } else {
            console.log("result", result);
            let adminTimeOffRequestSnapshotResult = result.torSnapshotObject;
            let scrubbedAdminTimeOffRequestSnapshot = getScrubbedTimeOffRequestSnapshot(
              adminTimeOffRequestSnapshotResult
            );
            dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearch(adminTimeOffRequestSnapshotResult));
            dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
            applySearch(newSearchStringTrimmed, [scrubbedAdminTimeOffRequestSnapshot]);
            dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(true));
          }
        })
        .catch((err) => {
          console.error("error happened!", err);
          dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
          dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(false));
        });
    } else {
      searchTimeOffRequests(newSearchStringTrimmed, applySearch, isAdminPageRef.current);
    }
  };

  const handleClickAdminTimeOffRequestFoundByReferenceNumberSearchDialogOpen = () => {
    dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(true));
  };

  const handleClickAdminTimeOffRequestFoundByReferenceNumberSearchDialogClose = () => {
    dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(false));
    dispatch(updateAdminTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(null));
  };

  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.TIME_OFF_ADMINISTRATION];
  const breadcrumbsLink = [HOME_PATH];
  const renderDesktopTORAdminPage = () => {
    return (
      <>
        <StarsBreadcrumbs
          isConfirmationRequired={false}
          breadcrumbsList={breadcrumbsList}
          breadcrumbsLinks={breadcrumbsLink}
        />
        <TimeOffFilterFormDesktop
          title={"Time Off Administration"}
          onDateTypeFilterChange={handleDateTypeFilterChange}
          onStatusFilterChange={handleStatusFilterChange}
          isStatusSelectAllChecked={isStatusSelectAllChecked}
          isStatusSelectAllIndeterminate={isStatusSelectAllIndeterminate}
          startDateFilter={getStartDateFilterSafely()}
          onStartDateFilterChange={handleStartDateFilterChange}
          endDateFilter={getEndDateFilterSafely()}
          onEndDateFilterChange={handleEndDateFilterChange}
          onTimeOffTypeFilterChange={handleTimeOffTypeFilterChange}
          isTimeOffTypeSelectAllChecked={isTimeOffTypeSelectAllChecked}
          isTimeOffTypeSelectAllIndeterminate={isTimeOffTypeSelectAllIndeterminate}
          onSearchChange={handleSearchChange}
          onSearchBlur={handleSearchBlur}
          onSearchEnterKeyPressed={handleSearchEnterKeyPressed}
          onSearchCancel={handleSearchCancel}
          onSearchSubmit={handleSearchSubmit}
          isDesktopView={props.isDesktopView}
          stations={getMultiStationAdminStationList(userSession)}
          featureConfig={props.featureConfig}
          workgroupFilters={workgroupFilters}
          allWorkgroupFilters={Object.keys(workgroupData)}
          onWorkgroupFilterChange={handleWorkgroupFilterChange}
          isWorkgroupSelectAllChecked={isWorkgroupSelectAllChecked}
          isWorkgroupSelectAllIndeterminate={isWorkgroupSelectAllIndeterminate}
          isAdminPage={true}
          onClickTimeOffRequestFoundByReferenceNumberSearchDialogOpen={
            handleClickAdminTimeOffRequestFoundByReferenceNumberSearchDialogOpen
          }
          onClickTimeOffRequestFoundByReferenceNumberSearchDialogClose={
            handleClickAdminTimeOffRequestFoundByReferenceNumberSearchDialogClose
          }
          stationFilter={getAdminStation(props.featureConfig.userStations, userSession, stationFilter)}
          onStationFilterChange={handleStationFilterChange}
          dateTypeFilter={adminTimeOffRequestData.dateTypeFilter}
          startDateFilterErrors={adminTimeOffRequestData.startDateFilterErrors}
          endDateFilterErrors={adminTimeOffRequestData.endDateFilterErrors}
          timeOffTypeFilters={adminTimeOffRequestData.timeOffTypeFilters}
          timeOffStatusFilters={adminTimeOffRequestData.statusFilters}
          searchString={adminTimeOffRequestData.searchString}
          searchStringErrors={adminTimeOffRequestData.searchStringErrors}
          timeOffRequestFoundByReferenceNumberSearchIsLoading={
            adminTimeOffRequestData.adminTimeOffRequestFoundByReferenceNumberSearchIsLoading
          }
          timeOffRequestFoundByReferenceNumberSearch={
            adminTimeOffRequestData.adminTimeOffRequestFoundByReferenceNumberSearch
          }
          timeOffRequestFoundByReferenceNumberSearchIsDialogOpen={
            adminTimeOffRequestData.adminTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen
          }
        />
        <EmployeeTimeOffRequestTable
          isDesktopView={props.isDesktopView}
          timeOffRequests={isSearch ? searchedStationTimeOffRequest : filteredStationTimeOffRequest}
          isAdminPage={true}
          isFirstLoad={isFirstLoad}
          isLoading={isLoading}
          isError={isError}
          errorObject={errorObject}
          station={stationFilterRef.current}
          torTableDesktopRef={torTableDesktopRef}
        />
      </>
    );
  };

  const renderMobileTORAdminPage = () => {
    return (
      <>
        <div className="page-error">{ADMIN_MOBILE_WARNING_MESSAGE}</div>
      </>
    );
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div className="App-contents" data-testid="admin-time-off-pages">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderDesktopTORAdminPage() : renderMobileTORAdminPage()}</>
      )}
    </div>
  );
};

export default withStyles(styles)(TimeOffAdministrationPage);
